import {
    AuthLocalStorageKeys,
    AuthSessionStorageKeys,
    AuthStep,
    type CustomDiscountRegisterData,
    eraseCookie,
    getCookie,
    LoginError,
    setAuthCookie,
    TermsStatus,
    type UserCredential,
    type UserInfo,
    UserType,
    type Utm,
} from './types';
import { customDataDefault } from './statics';
import Regex from '@static/regex';
import {
    type AcquaintanceMethod,
    CampaignApiApiSetUUIDCampaignPost,
    type CampaignRequest,
    CampaignType,
    type ChargeCreditReasons,
    type DomesticCityDto,
    LoginApiApiChangePasswordPatch,
    LoginApiApiForgotpasswordPatch,
    LoginApiApiLoginGet,
    LoginApiMigrateAppTokenPost,
    LoginApiRefreshPost,
    type LoginResponse,
    type ProvinceDto,
    RegisterApiApiRegisterPost,
    type RegisterRequest,
    type RegisterResponse,
    ServiceDiscountApiApiServiceDiscountsGet,
    ThpApiApiTHPEzamLoginPost,
    TokenApiApiTokenPost,
    TokenApiApiTokenSendGet,
    UserApiApiAssociateUUIDGet,
    UserApiApiChangeUserPatch,
    UserApiApiGetAcquaintanceMethodsGet,
    UserApiApiGetDomesticCitiesProvinceIdGet,
    UserApiApiGetProvincesGet,
    UserApiApiGetuserGet,
    UserApiApiGetWhyMrBilitGet,
    UserTransactionApiApiUserTransactionsGet,
    type UserTransactionView,
    type WhyMrBilit,
} from '@clients/AuthApi';
import {
    BillsApiApiBillsUserHasPurchaseGet,
    type UserScoreLevelRange,
    UserScoreLevels,
    UserScoresApiApiUserScoresBriefGet,
    UserScoresApiApiUserScoresLevelRangesGet,
    UserScoresApiApiUserScoresVariationGet,
    type UtmAnalyticsParamRequest,
    type VariationUserLevelResponse,
} from '@clients/BillApi';
import {
    type SocialMediaAccountResponse,
    SocialMediaApiApiSocialMediaDeleteSocialMediaAccountDelete,
    SocialMediaApiApiSocialMediaGet,
    type SocialMediaJoinLinkResponse,
    UserMessagesApiApiCheckUnreadGet,
} from '@clients/NotifierApi';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { StatusReportSummaryApiUserReportIsCreditPaymentLimitedGet } from '@clients/AccountingApi';
import { Gender } from '@store/form';

export class AuthStore {
    //#region state

    /**
     * indicate that the current user is admin logged in via panel or not
     *
     * @type boolean
     * @memberof AuthStore
     */

    adminSession = false;
    customDiscounts = customDataDefault;

    /**
     * Map of error messages to specific field of the login/signup process
     *
     * @type {{ [k: string]: string }}
     * @memberof AuthStore
     */
    error: Partial<Record<LoginError, string>> = {};
    vpn = false;

    /**
     * Jwt token for the logged in user
     *
     * @type {string | null}
     * @memberof AuthStore
     */
    credentials: UserCredential[] | null = null;

    /**
     * Information of the user
     *
     * @type {Partial<UserInfo> | null}
     * @memberof AuthStore
     */
    userInfo: UserInfo | null = null;

    /**
     * Indicates whether the authentication page is loading or not
     *
     * @type {boolean}
     * @memberof AuthStore
     */
    loading = false;

    /**
     * The unique identifier for the user, while he is not logged in
     *
     * @type {string | null}
     * @memberof AuthStore
     */
    localUuid: string | null = null;

    /**
     * Indicates whether the user information is loading or not
     *
     * @type {boolean}
     * @memberof AuthStore
     */
    userIsLoading = false;

    /**
     * The current score of the user
     *
     * @type {(number | null)}
     * @memberof AuthStore
     */
    score: number | null = null;

    /**
     * The current discount of score of the user
     *
     * @type {(number | null)}
     * @memberof AuthStore
     */
    scoreDiscountAmount: number | null = null;

    /**
     * The current loyalty level of the user
     *
     * @type {(string | null)}
     * @memberof AuthStore
     */
    level: UserScoreLevels = UserScoreLevels.Level1;
    levelDetails: VariationUserLevelResponse = {};

    totalTickets = 0;

    scoreDomains: UserScoreLevelRange[] = [];

    /**
     * The list of the credit transactions of the user
     */
    creditTransactions: UserTransactionView[] = [];

    limitPayment = false;
    loadingUserLimit = false;

    /**
     * Indicates whether the loading credit transaction
     */
    creditTransactionsLoading = false;

    /**
     * Indicates whether the terms and conditions are accepted by the user, or not
     *
     * @type {(TermsStatus | null)}
     * @memberof AuthStore
     */
    termsStatus: TermsStatus | null = null;

    selectedCredentialIndex = 0;

    get credit() {
        return this.userInfo?.credit || 0;
    }

    /**
     * check user has any purchase or not if not checked is null
     *
     * @type {(boolean | null)}
     * @memberof AuthStore
     */
    userHasPurchase: boolean | null = null;
    acquaintanceMethods: AcquaintanceMethod[] = [];
    mrbilitReasons: WhyMrBilit[] = [];
    provinces: ProvinceDto[] = [];
    provinceCities: DomesticCityDto[] = [];
    provinceCitiesLoading = false;
    socialMediaAccounts: SocialMediaAccountResponse[] = [];
    socialMediaLinks: SocialMediaJoinLinkResponse[] = [];
    socialMediaLoading = true;
    signupMac: string | null = null;
    canGetDoubleScore = false;
    giftMac = '';
    giftCode = '';
    userHasUnreadMessage = false;
    initialContact = {
        mobile: '',
        email: '',
        tel: '',
    };

    webAppDefaultToken: string | null = null;

    webAppSourceId: number | null = null;
    sessionId = '';
    //#endregion

    /**
     * MUTATION — Sets the error message for a specific field of the login/signup process.
     *
     * @param error {{ key: string; text: string }}
     * @memberof AuthStore
     */
    setError(error: { key: LoginError; text: string }) {
        this.error[error.key] = error.text;
    }

    setVpn(enabled: boolean) {
        this.vpn = enabled;
    }

    /**
     * save gift mac and gift code for discounts
     *
     * @param {{ mac: string; code: string }} { mac, code }
     * @memberof AuthStore
     */
    giftsSetter({ mac, code }: { mac: string; code: string }) {
        this.giftMac = mac;
        this.giftCode = code;
    }

    /**
     * Specifies a token that will be used for unauthenticated users
     */
    get defaultToken(): string {
        const { defaultToken } = useWhiteLabel();
        return this.webAppDefaultToken || defaultToken.value;
    }

    /**
     * Specifies the source id of the current application
     */
    get siteSourceId(): number {
        const { sourceId } = useWhiteLabel();
        return this.webAppSourceId || sourceId.value;
    }

    get authStorage() {
        return this.adminSession ? sessionStorage : localStorage;
    }

    get eventMetrics() {
        return {
            isLogin: this.userInfo?.id ? 1 : 0,
            creationSource: this.siteSourceId,
            appVersion: null,
            uuid: this.uuid,
            vpn: this.vpn,
        };
    }

    /**
     * Clears all errors set
     */
    clearErrors() {
        this.error = {};
    }

    setSelectedCredentialIndex(val: number) {
        this.selectedCredentialIndex = val;
        this.authStorage.setItem(
            AuthLocalStorageKeys.selectedCredentialIndex,
            val.toString()
        );
    }

    setLoginInfo(info: Partial<LoginResponse> & Partial<RegisterResponse>) {
        this.userInfo = info
            ? {
                  mobile: info.userMobile,
                  email: info.userEmail,
                  userGroup: info.userGroup,
              }
            : null;
        const expireDateMilSec = dayConfig
            .gregory()
            .add(59, 'day')
            .startOf('day')
            .valueOf();
        let credentials: UserCredential[] = [
            {
                type: UserType.User,
                title: info.title || '',
                token: info.token || '',
                uuid: info.uuid || '',
                expireDateMilSec,
            },
        ];
        if (info.organizations?.length) {
            credentials.push(
                ...info.organizations.map(organization => ({
                    type: UserType.Organization,
                    title: organization.title || '',
                    token: organization.token || '',
                    uuid: organization.uuid || '',
                    expireDateMilSec,
                }))
            );
        } else if (info.userGroup === 34) {
            credentials = [
                {
                    title: info.title ?? '',
                    type: UserType.Organization,
                    token: info.token ?? '',
                    uuid: info.uuid ?? '',
                    expireDateMilSec,
                },
            ];
        } else if (info.userGroup === 40) {
            credentials = [
                {
                    title: info.title ?? '',
                    type: UserType.Intermediate,
                    token: info.token ?? '',
                    uuid: info.uuid ?? '',
                    expireDateMilSec,
                },
            ];
        }
        // if the admins logged in to a user account,
        // the user data would be stored in sessionStorage rather than localStorage
        // meanwhile, admin's main account data would be in localStorage
        this.credentials = credentials;
        this.authStorage.setItem(
            AuthLocalStorageKeys.credentials,
            JSON.stringify(credentials)
        );
        setAuthCookie('JWT', info.token || '', 60);
    }

    resetCustomDiscount() {
        this.customDiscounts = customDataDefault;
    }

    async loadUserCustomDiscounts() {
        const { featureFlags, loadPromise } = storeToRefs(usePostHog());
        await loadPromise.value;
        const isFeatureEnabled =
            featureFlags.value?.['custom-discount-enabled'];
        const storedData = this.authStorage.getItem(
            AuthLocalStorageKeys.customDiscountsInfo
        );
        const {
            data: storedDiscounts,
            date,
            userId,
        }: CustomDiscountRegisterData = JSON.parse(storedData || '{}');

        const isExpired =
            !Number(date) ||
            dayConfig.gregory(date).add(1, 'day').isBefore(dayConfig.gregory());
        const isSameUser = userId === this.userInfo?.id;
        const isInvalidStoredData = !isSameUser || isExpired;
        if (!isSameUser) {
            return this.resetCustomDiscount();
        }
        if (!isFeatureEnabled) {
            return this.resetCustomDiscount();
        }

        if (isInvalidStoredData && this.isOrganizationalUser) {
            try {
                const { data } =
                    await ServiceDiscountApiApiServiceDiscountsGet();
                data.data?.forEach(({ serviceType, discountPercent }) => {
                    if (serviceType) {
                        this.customDiscounts[serviceType] =
                            discountPercent ?? 0;
                    }
                });

                const cache: CustomDiscountRegisterData = {
                    data: this.customDiscounts,
                    date: Date.now(),
                    userId: this.userInfo?.id || 0,
                };
                this.authStorage.setItem(
                    AuthLocalStorageKeys.customDiscountsInfo,
                    JSON.stringify(cache)
                );
            } catch {
                this.resetCustomDiscount();
            }
        } else if (storedDiscounts) {
            this.customDiscounts = storedDiscounts;
        }
    }

    get needTokenMigration() {
        return this.webAppSourceId === 15 || this.webAppSourceId === 16;
    }

    logout() {
        // adminSession value would be lost in resetState func
        const resetToAdminMainAccount = this.adminSession;
        eraseCookie('JWT');
        this.authStorage.removeItem(AuthLocalStorageKeys.credentials);
        this.authStorage.removeItem(
            AuthLocalStorageKeys.selectedCredentialIndex
        );

        this.resetState();
        useTrackingStore().resetState();
        const formStore = useFormStoreIfInstalled();
        if (formStore) {
            formStore.setPrevPax([]);
            formStore.prevPaxLoadingStatus = LoadingStatus.None;
        }
        this.initialContact = {
            tel: '',
            email: '',
            mobile: '',
        };
        this.authStorage.removeItem(
            AuthLocalStorageKeys.lastCheckUserHasPurchase
        );
        sessionStorage.removeItem(AuthLocalStorageKeys.lastCheckout);
        if (resetToAdminMainAccount) {
            // logout admin from user's account
            this.reset();
        }
    }

    initSession() {
        const prevSessionId = sessionStorage.getItem(
            AuthSessionStorageKeys.sessionId
        );
        if (prevSessionId) {
            this.sessionId = prevSessionId;
        } else {
            const newSession = `session_${generateUUID()}`;
            this.sessionId = newSession;
            sessionStorage.setItem(
                AuthSessionStorageKeys.sessionId,
                newSession
            );
        }
    }

    /**
     * reset state of auth module
     *
     * @memberof AuthStore
     */
    resetState() {
        const emptyState = new AuthStore() as Partial<AuthStore>;
        delete emptyState.localUuid;
        const stateEntries = Object.entries(
            Object.getOwnPropertyDescriptors(emptyState)
        );
        const state = stateEntries.reduce(
            (prev, [currKey, currValue]) => ({
                ...prev,
                [currKey]: currValue.value,
            }),
            {} as any
        );
        Object.keys(state).forEach(key => {
            (this as any)[key] = state[key];
        });
    }

    /**
     * Loads token and uuids from local storage or cookies
     *
     * @memberof AuthStore
     */
    reset(prevUuid?: string) {
        const adminSessionCredentials = JSON.parse(
            sessionStorage.getItem(AuthLocalStorageKeys.credentials) || '[]'
        );
        this.adminSession =
            !!adminSessionCredentials?.[this.selectedCredentialIndex]?.token;
        if (this.authStorage.getItem(AuthLocalStorageKeys.localUUID)) {
            this.localUuid = this.authStorage.getItem(
                AuthLocalStorageKeys.localUUID
            );
        }

        if (this.authStorage.getItem(AuthLocalStorageKeys.termsStatus)) {
            this.termsStatus = this.authStorage.getItem(
                AuthLocalStorageKeys.termsStatus
            ) as TermsStatus;
        }

        this.selectedCredentialIndex = parseInt(
            this.authStorage.getItem(
                AuthLocalStorageKeys.selectedCredentialIndex
            ) || '0'
        );

        // if we are in admin session in the initial state we going to set this.credentials to the aimed user credentials
        this.credentials = JSON.parse(
            this.authStorage.getItem(AuthLocalStorageKeys.credentials) || '[]'
        );

        const cookieToken = getCookie('JWT');
        const cookiePlayerID = getCookie('PlayerID');
        this.webAppDefaultToken = getCookie('DefaultToken') ?? null;
        this.webAppSourceId = getCookie('SourceId')
            ? +getCookie('SourceId')!
            : null;
        if (!this.credentials?.length && cookieToken && cookiePlayerID) {
            // for load user by setting cookie info
            // if there was a way to read playerId expire date from cookie it should be add here
            this.credentials = [
                {
                    title: '',
                    type: UserType.User,
                    token: cookieToken,
                    uuid: cookiePlayerID,
                    expireDateMilSec: null,
                },
            ];
        }
        if (!this.localUuid) {
            if (cookiePlayerID) {
                this.localUuid = cookiePlayerID;
                this.authStorage.setItem(
                    AuthLocalStorageKeys.localUUID,
                    this.localUuid || ''
                );
                eraseCookie('PlayerID');
            } else {
                const uuid = prevUuid || generateUUID();
                this.authStorage.setItem(AuthLocalStorageKeys.localUUID, uuid);
                this.localUuid = uuid;
            }
        }
    }

    get levelDomains(): { min?: number; max?: number }[] {
        return [
            {
                min: 31,
            },
            {
                min: 11,
                max: 30,
            },
            {
                min: 6,
                max: 10,
            },
            {
                max: 5,
            },
        ];
    }

    get scoreDomainsWithDefault(): UserScoreLevelRange[] {
        if (this.scoreDomains.length) {
            return this.scoreDomains;
        } else {
            return [
                {
                    Min: 0,
                    Max: 0,
                },
                {
                    Min: 0,
                    Max: 0,
                },
                {
                    Min: 0,
                    Max: 0,
                },
                {
                    Min: 0,
                    Max: 0,
                },
            ];
        }
    }

    async loadScoreDomains() {
        if (this.scoreDomains.length) {
            return;
        }
        const { data } = await UserScoresApiApiUserScoresLevelRangesGet();
        if (data[0].Level === UserScoreLevels.Level1) {
            data.reverse();
        }
        this.scoreDomains = data;
    }

    get levelIndex(): number {
        return [
            UserScoreLevels.Level4,
            UserScoreLevels.Level3,
            UserScoreLevels.Level2,
            UserScoreLevels.Level1,
        ].indexOf(this.level);
    }

    get levelTitle(): string {
        switch (this.levelIndex) {
            case 0:
                return 'الماس';
            case 1:
                return 'طلایی';
            case 2:
                return 'نقره‌ای';
            case 3:
                return 'برنزی';
            default:
                return '';
        }
    }

    async getSocialMedia(force = false) {
        if (!force && this.socialMediaLinks.length) {
            return;
        }
        this.socialMediaLoading = true;
        const { data } = await SocialMediaApiApiSocialMediaGet();
        this.socialMediaAccounts = data.accounts || [];
        this.socialMediaLinks = data.socialMediaJoinLinks || [];
        this.socialMediaLoading = false;
    }

    async deleteSocialMedia(id: number) {
        await SocialMediaApiApiSocialMediaDeleteSocialMediaAccountDelete({
            id,
        });
        await this.getSocialMedia(true);
    }

    get accountName(): string {
        if (!this.credentials) {
            return 'حساب کاربری';
        }
        const userCredential = this.credentials[this.selectedCredentialIndex];
        switch (userCredential?.type) {
            case UserType.User:
                return userCredential.title;
            case UserType.Organization:
            case UserType.Intermediate:
                return `شرکت ${userCredential.title}`;
            default:
                return 'حساب کاربری';
        }
    }

    get hasMultipleAccounts(): boolean {
        return (this.credentials?.length || 0) > 1;
    }

    /**
     * Sets the local unique identifier, that is used to identify user before being logged in.
     *
     * @param uuid {string | null}
     * @memberof AuthStore
     */
    setLocalUuid(uuid: string | null) {
        this.localUuid = uuid;
        this.authStorage.setItem(
            AuthLocalStorageKeys.localUUID,
            this.localUuid || ''
        );
    }

    updateCurrentToken(token: string) {
        if (
            !this.credentials ||
            !this.credentials[this.selectedCredentialIndex]
        ) {
            return;
        }
        this.credentials[this.selectedCredentialIndex].token = token;
    }

    /**
     * GETTER - Indicates whether the user is logged in or not
     *
     * @memberof AuthStore
     */
    get loggedIn() {
        return !!this.credentials?.length;
    }

    /**
     * GETTER - Indicates whether the user is an organizational user, or not
     *
     * @memberof AuthStore
     */
    get isOrganizationalUser() {
        return (
            this.credentials?.[this.selectedCredentialIndex]?.type ===
            UserType.Organization
        );
    }

    get currentUserType(): UserType | undefined {
        return this.credentials?.[this.selectedCredentialIndex]?.type;
    }

    get currentUserTypeString(): string {
        switch (this.currentUserType) {
            case UserType.Organization:
                return 'Organization';
            case UserType.Intermediate:
                return 'Intermediate';
            default:
                return 'User';
        }
    }

    /**
     * GETTER - Returns the unique token dedicated to authenticated user
     *
     * @memberof AuthStore
     */
    get token() {
        return (
            this.credentials?.[this.selectedCredentialIndex]?.token ||
            this.defaultToken
        );
    }

    validateUsername(username: string, only?: 'mobile' | 'email') {
        const input = convertPersianNumbers(username.trim());
        const emailError = 'ایمیل وارد شده معتبر نیست.';
        const mobileError = 'شماره موبایل وارد شده معتبر نیست.';
        const globalError = 'ایمیل یا شماره موبایل معتبر نیست';
        if (Regex.mobile.full.test(input)) {
            if (only === 'email') {
                this.setError({
                    key: LoginError.MobileOrEmail,
                    text: emailError,
                });
                return '';
            } else {
                this.clearErrors();
                return input;
            }
        }
        if (Regex.email.full.test(input)) {
            if (only === 'mobile') {
                this.setError({
                    key: LoginError.MobileOrEmail,
                    text: mobileError,
                });
                return '';
            } else {
                this.clearErrors();
                return input;
            }
        }
        this.setError({
            key: LoginError.MobileOrEmail,
            text:
                only === 'email'
                    ? emailError
                    : only === 'mobile'
                      ? mobileError
                      : globalError,
        });

        return '';
    }

    /**
     * GETTER - Returns the unique user id, that is either the local or login unique id, based on current authentication status
     *
     * @memberof AuthStore
     */
    get uuid() {
        return (
            this.credentials?.[this.selectedCredentialIndex]?.uuid ||
            this.localUuid
        );
    }

    /**
     * ACTION - Checks the password whether it is correct or not
     *
     * @param {{ password: string; noSuccess: boolean }} payload
     * @memberof AuthStore
     */
    // Step 2.1. Checking password
    async loginByPassword(payload: { password: string; username: string }) {
        this.loading = true;
        let email = '';
        let mobile = '';
        if (Regex.email.full.test(payload.username)) {
            email = payload.username;
        } else {
            mobile = payload.username;
        }
        try {
            const { data } = await LoginApiApiLoginGet({
                username: payload.username || undefined,
                password: payload.password,
                mobile: mobile || undefined,
                email: email || undefined,
                source: this.siteSourceId,
            });
            if (data.authenticated) {
                // Success
                this.setLoginInfo(data);
                const formStore = useFormStoreIfInstalled();
                if (formStore) {
                    formStore.prevPaxLoadingStatus = LoadingStatus.None;
                }
                this.clearErrors();
                if (data.organizations?.length) {
                    this.loadUser(true);
                    return AuthStep.SwitchAccount;
                } else {
                    return this.success();
                }
            } else {
                this.setError({
                    key: LoginError.CurrentPassword,
                    text: 'نام کاربری یا رمز عبور اشتباه است',
                });
                throw new Error(data.error ?? '');
            }
        } catch (ex) {
            const e = ex as any;
            const errorText =
                e.response.data.error || 'خطایی در ارتباط با سرور رخ‌داد!';

            swal({
                title: errorText,
                text: 'لطفا دوباره تلاش کنید.',
                confirmButtonText: 'تایید',
            });
            throw new Error(errorText);
        } finally {
            this.loading = false;
        }
    }

    async loginByThirdParty(payload: { token: string; whiteLabel: string }) {
        this.loading = true;
        const currentWhiteLabel = payload.whiteLabel;
        const formStore = useFormStoreIfInstalled();
        switch (currentWhiteLabel) {
            case 'ezam':
                if (this.token) {
                    this.logout();
                }
                try {
                    if (!payload.token) {
                        return;
                    }
                    const { data } = await ThpApiApiTHPEzamLoginPost({
                        ezamLoginRequest: { token: payload.token },
                    });
                    if (data.authenticated) {
                        // Success
                        this.setLoginInfo(data);
                        if (data.organizations?.length) {
                            this.loadUser(true);
                            useAuth().switchAccount();
                        } else {
                            this.success();
                        }
                        if (formStore) {
                            formStore.prevPaxLoadingStatus = LoadingStatus.None;
                        }
                        this.clearErrors();
                    }
                } catch (e) {
                    reportException(e);
                } finally {
                    this.loading = false;
                }
        }
    }

    /**
     * ACTION - Sign ups new user to system
     *
     * @param {{password: string; firstName: string; lastName: string; email: string; noEmail: boolean;}} payload
     * @memberof AuthStore
     */

    signupValidation(payload: {
        password: string;
        firstName: string;
        lastName: string;
        email?: string;
        gender: Gender;
        mobile: string;
    }) {
        this.clearErrors();

        if (payload.password && payload.password.length < 6) {
            this.setError({
                key: LoginError.Password,
                text: 'رمز ورود باید حداقل ۶ رقم باشد',
            });
        }
        if (payload.gender === Gender.None) {
            this.setError({
                key: LoginError.Gender,
                text: 'جنسیت خود را مشخص کنید.',
            });
        }

        if (
            payload.firstName &&
            !/^[\u0600-\u0660\u0670-\u06F0\s‌]+$/.test(payload.firstName)
        ) {
            this.setError({
                key: LoginError.FirstName,
                text: 'لطفا نام را با حروف فارسی وارد کنید',
            });
        }

        if (
            payload.lastName &&
            !/^[\u0600-\u0660\u0670-\u06F0\s‌]+$/.test(payload.lastName)
        ) {
            this.setError({
                key: LoginError.LastName,
                text: 'لطفا نام خانوادگی را با حروف فارسی وارد کنید',
            });
        }

        if (payload.email && !Regex.email.full.test(payload.email)) {
            this.setError({
                key: LoginError.Email,
                text: 'لطفا ایمیل را به درستی وارد نمایید',
            });
        }
    }

    async signup(payload: {
        password: string;
        firstName: string;
        lastName: string;
        email?: string;
        male?: boolean;
        mobile: string;
        token: string;
    }) {
        this.clearErrors();

        // Validation
        let error = false;
        if (payload.password.length < 6) {
            this.setError({
                key: LoginError.Password,
                text: 'رمز ورود باید حداقل ۶ رقم باشد',
            });
            error = true;
        }
        if (payload.male === undefined) {
            this.setError({
                key: LoginError.Gender,
                text: 'جنسیت خود را مشخص کنید.',
            });
        }

        if (!/^[\u0600-\u0660\u0670-\u06F0\s‌]+$/.test(payload.firstName)) {
            this.setError({
                key: LoginError.FirstName,
                text: 'لطفا نام را با حروف فارسی وارد کنید',
            });
            error = true;
        }

        if (!/^[\u0600-\u0660\u0670-\u06F0\s‌]+$/.test(payload.lastName)) {
            this.setError({
                key: LoginError.LastName,
                text: 'لطفا نام خانوادگی را با حروف فارسی وارد کنید',
            });
            error = true;
        }

        if (payload.email && !Regex.email.full.test(payload.email)) {
            this.setError({
                key: LoginError.Email,
                text: 'لطفا ایمیل را به درستی وارد نمایید',
            });
            error = true;
        }

        if (error) {
            throw new Error('لطفا اطلاعات واردشده را بررسی نمایید');
        }

        this.loading = true;
        try {
            const { data } = await RegisterApiApiRegisterPost({
                registerRequest: {
                    email: payload.email ?? null,
                    family: payload.lastName,
                    name: payload.firstName,
                    mobile: payload.mobile,
                    password: payload.password,
                    sourceId: this.siteSourceId,
                    token: payload.token,
                    male: payload.male ?? null,
                    magicSignUpMac: this.signupMac,
                    // before auth api changes following params never sended to auth
                    aquiantanceMethodId: null,
                    birthDate: null,
                    companyName: null,
                    englishLastName: null,
                    englishTitle: null,
                    iranian: true,
                    nationalCode: null,
                    nationality: null,
                    passportExpiry: null,
                    passportNumber: null,
                    phone: null,
                    residenceProvince: null,
                    siteId: undefined,
                    whyMrBilitId: null,
                },
            });
            this.setLoginInfo(data);
            const formStore = useFormStoreIfInstalled();
            if (formStore) {
                formStore.prevPaxLoadingStatus = LoadingStatus.None;
            }
            return await this.success();
        } catch (ex) {
            const e = ex as any;
            const errorText =
                e.response.data.error || 'خطایی در ارتباط با سرور رخ‌داد!';
            swal({
                title: errorText,
                text: 'لطفا دوباره تلاش کنید.',
                confirmButtonText: 'تایید',
            });
            throw new Error(errorText);
        } finally {
            this.loading = false;
        }
    }

    async getAcquaintanceMethods(force = false) {
        try {
            if (!force && this.acquaintanceMethods.length) {
                return;
            }
            const { data } = await UserApiApiGetAcquaintanceMethodsGet();
            this.acquaintanceMethods = data;
        } catch (e) {
            reportException(e);
        }
    }

    async getMrbilitReasons(force = false) {
        try {
            if (!force && this.mrbilitReasons.length) {
                return;
            }
            const { data } = await UserApiApiGetWhyMrBilitGet();
            this.mrbilitReasons = data;
        } catch (e) {
            reportException(e);
        }
    }

    async getProvinces(force = false) {
        try {
            if (!force && this.provinces.length) {
                return;
            }
            const { data } = await UserApiApiGetProvincesGet();
            this.provinces = data;
        } catch (e) {
            reportException(e);
        }
    }

    async getProvinceCities(provinceId: number) {
        try {
            this.provinceCitiesLoading = true;
            const { data } = await UserApiApiGetDomesticCitiesProvinceIdGet({
                provinceId,
            });
            this.provinceCities = data;
        } catch (e) {
            reportException(e);
        } finally {
            this.provinceCitiesLoading = false;
        }
    }

    /**
     * ACTION - Checks the login token sent to user's mobile via sms
     *
     * @param {{ token: string; noSuccess: boolean }} payload
     * @memberof AuthStore
     */
    async loginByToken(payload: { token: string; username: string }) {
        this.loading = true;
        try {
            const { data } = await LoginApiApiLoginGet({
                token: payload.token,
                mobile: payload.username,
                source: this.siteSourceId,
            });
            if (data.authenticated) {
                // Success
                this.setLoginInfo(data);
                const formStore = useFormStoreIfInstalled();
                if (formStore) {
                    formStore.prevPaxLoadingStatus = LoadingStatus.None;
                }
                if (data.organizations?.length) {
                    this.loadUser(true);
                    return AuthStep.SwitchAccount;
                } else {
                    return this.success();
                }
            } else {
                const errorText =
                    data.error || 'خطایی در ارتباط با سرور رخ‌داد!';
                // Failure
                swal({
                    title: errorText,
                    text: 'لطفا دوباره تلاش کنید.',
                    confirmButtonText: 'تایید',
                });
                throw new Error(errorText);
            }
        } catch (ex) {
            const e = ex as any;
            const errorText =
                e.response.data.error || 'خطایی در ارتباط با سرور رخ‌داد!';
            swal({
                title: errorText,
                text: 'لطفا دوباره تلاش کنید.',
                confirmButtonText: 'تایید',
            });
            throw new Error(errorText);
        } finally {
            this.loading = false;
        }
    }

    /**
     * ACTION - Changes the user's password by token
     *
     * @param {{ password: string; passwordConfirm: string }} payload
     * @memberof AuthStore
     */

    passWordValidation(payload: {
        password: string;
        passwordConfirm?: string;
    }) {
        this.clearErrors();
        if (payload.password.length < 6) {
            this.setError({
                key: LoginError.NewPassword,
                text: 'رمز ورود باید حداقل ۶ رقم باشد',
            });
            return false;
        }

        if (!payload.passwordConfirm) {
            return;
        }

        if (payload.password !== payload.passwordConfirm) {
            this.setError({
                key: LoginError.NewPasswordConfirm,
                text: 'رمز ورود با تکرار آن مطابقت ندارد',
            });
            return false;
        }
    }

    async changePasswordByToken(payload: {
        password: string;
        passwordConfirm: string;
        mobile: string;
        token: string;
    }): Promise<false | AuthStep> {
        this.clearErrors();

        // Validation
        if (payload.password.length < 6) {
            this.setError({
                key: LoginError.NewPassword,
                text: 'رمز ورود باید حداقل ۶ رقم باشد',
            });
            return false;
        }

        if (payload.password !== payload.passwordConfirm) {
            this.setError({
                key: LoginError.NewPasswordConfirm,
                text: 'رمز ورود با تکرار آن مطابقت ندارد',
            });
            return false;
        }

        this.loading = true;
        try {
            const { data } = await LoginApiApiForgotpasswordPatch({
                forgetPasswordRequest: {
                    token: payload.token ?? null,
                    mobile: payload.mobile ?? null,
                    newPassword: payload.password ?? null,
                    email: null,
                    sourceId: 2,
                },
            });
            if (data) {
                await this.loginByPassword({
                    password: payload.password,
                    username: payload.mobile,
                });
                this.loading = false;
                if (this.hasMultipleAccounts) {
                    return AuthStep.SwitchAccount;
                } else {
                    return this.success();
                }
            } else {
                swal({
                    title: 'خطایی در ارتباط با سرور رخ‌داد!',
                    text: 'لطفا دوباره تلاش کنید.',
                    confirmButtonText: 'تایید',
                });
                this.loading = false;
                throw new Error('خطایی در ارتباط با سرور رخ‌داد!');
            }
        } catch (e) {
            swal({
                title: 'خطایی در ارتباط با سرور رخ‌داد!',
                text: 'لطفا دوباره تلاش کنید.',
                confirmButtonText: 'تایید',
            });
            this.loading = false;
            throw e;
        }
    }

    /**
     * ACTION - Changes the user's password
     *
     * @param {{ oldPassword: string, newpassword: string; newPasswordConfirm: string }} payload
     * @memberof AuthStore
     */
    async changePassword(payload: {
        oldPassword: string;
        newPassword: string;
        newPasswordConfirm: string;
    }): Promise<boolean> {
        this.clearErrors();

        // Validation
        if (payload.newPassword.length < 6) {
            this.setError({
                key: LoginError.NewPassword,
                text: 'رمز ورود باید حداقل ۶ رقم باشد',
            });
            return false;
        }

        if (payload.newPassword !== payload.newPasswordConfirm) {
            this.setError({
                key: LoginError.NewPasswordConfirm,
                text: 'رمز ورود با تکرار آن مطابقت ندارد',
            });
            return false;
        }

        this.loading = true;
        try {
            const { data } = await LoginApiApiChangePasswordPatch({
                username: this.userInfo!.username || '',
                oldPassword: payload.oldPassword,
                newPassword: payload.newPassword,
                source: this.siteSourceId,
            });
            if (data) {
                this.loading = false;
                await swal({
                    title: 'با موفقیت انجام شد',
                    icon: 'success',
                    denyButtonText: 'تایید',
                });
                return true;
            }
            return false;
        } catch (e) {
            this.loading = false;
            throw e;
        }
    }

    /**
     * check user has purchase or not
     *
     * @memberof AuthStore
     */
    async checkUserHasPurchase() {
        if (this.userHasPurchase || !this.loggedIn) {
            return;
        }
        try {
            const { data } = await BillsApiApiBillsUserHasPurchaseGet();
            this.userHasPurchase = !!data.UserHasPurchase;
            if (this.userHasPurchase) {
                this.authStorage.setItem(
                    AuthLocalStorageKeys.lastCheckUserHasPurchase,
                    'true'
                );
            }
        } catch (e) {
            reportException(e);
        }
    }

    /**
     * ACTION - Called when the login/signup process is successfully done. It shows a success message and closes the dialog
     *
     * @memberof AuthStore
     */
    async success() {
        await this.loadUser(true);
        this.loadCredit();
        this.checkUserHasUnreadMessage();
        useInvoiceStore().assignBillToLoggedInUser(this.uuid);
        setTimeout(() => {
            if (
                this.userInfo?.userGroup === 34 &&
                this.termsStatus !== TermsStatus.Accepted
            ) {
                this.termsStatus = TermsStatus.NotAccepted;
            }
            this.associateUuid();
        }, 2000);
        return AuthStep.Success;
    }

    webengageLogin() {
        if (useDevice().isWebApp.value) {
            const { userInfo } = this;
            window.WebAppWebEngageChannel?.postMessage(
                JSON.stringify({
                    type: 'user-login',
                    userId: userInfo!.mobile,
                    userInfo,
                    LoggedInForm: false,
                })
            );
        } else {
            window.webengage?.onReady(() => {
                const analyticsStore = useAnalyticsStore();
                const { userInfo } = this;
                const cuid =
                    window.webengage?.state.getForever &&
                    window.webengage?.state.getForever()?.cuid;
                if (userInfo && (!cuid || cuid !== userInfo.mobile)) {
                    window.webengage?.user.login(userInfo.mobile);
                    analyticsStore.setWebengageUserInfo(userInfo);
                    window.webengage?.user.setAttribute('LoggedInForm', false);
                }
            });
        }
    }

    metrixLogin() {
        window.WebAppMetrixAuthChannel?.postMessage(
            JSON.stringify(this.userInfo)
        );
    }

    clarityLogin() {
        window.clarity?.('set', 'isLoggedIn', 'true');
    }

    gtmLogin(userId: number) {
        const gtm = useGtm();
        gtm?.push({
            event: 'login',
            userId,
        });
    }

    webengageLoginInForm(mobile: string) {
        const analyticsStore = useAnalyticsStore();
        if (useDevice().isWebApp.value) {
            const { userInfo } = this;
            window.WebAppWebEngageChannel?.postMessage(
                JSON.stringify({
                    type: 'user-login',
                    userId: mobile,
                    userInfo,
                    LoggedInForm: true,
                })
            );
            analyticsStore.trackWebengage('login-user-in-form');
        } else {
            window.webengage?.onReady(() => {
                const cuid =
                    window.webengage?.state.getForever &&
                    window.webengage?.state.getForever()?.cuid;
                if (!cuid) {
                    window.webengage?.user.login(mobile);
                    window.webengage?.user.setAttribute('LoggedInForm', true);
                    analyticsStore.trackWebengage('login-user-in-form');
                }
            });
        }
    }

    /**
     * ACTION - Checks the validation of signup or forgot-password token sent to user's mobile via sms
     *
     * @param mobile
     * @param {string} token
     * @memberof AuthStore
     */
    async checkToken(mobile: string, token: string): Promise<boolean | null> {
        this.loading = true;
        try {
            const { data } = await TokenApiApiTokenPost({
                tokenValidationRequest: {
                    token,
                    uniqueId: mobile,
                },
                source: this.siteSourceId,
            });
            if (data.valid) {
                // Success
                return data.userExists ?? false;
            } else {
                // Failure
                this.setError({
                    key: LoginError.Token,
                    text: 'کد ورود اشتباه است، لطفا مجددا تلاش نمایید',
                });
                return null;
            }
        } catch (e) {
            this.setError({
                key: LoginError.Token,
                text: 'کد ورود اشتباه است، لطفا مجددا تلاش نمایید',
            });
            reportException(e);
            return null;
        } finally {
            this.loading = false;
        }
    }

    /**
     * ACTION - Sends token to user's mobile via sms
     *
     * @memberof AuthStore
     */
    async sendToken(mobile: string) {
        if (mobile) {
            await TokenApiApiTokenSendGet({ mobile });
        }
    }

    /**
     * ACTION - Loads the credit of logged-in user
     */
    async loadCredit(forceReload = false) {
        if (this.loggedIn && (forceReload || this.credit === null)) {
            const { data } = await UserApiApiGetuserGet();
            this.userInfo = data;
        }
    }

    /**
     * increase user credit by getting amount and reason
     *
     * @param {number} amount
     * @param {ChargeCreditReasons} [reason]
     * @memberof AuthStore
     */
    increaseCredit(amount: number, reason?: ChargeCreditReasons) {
        const { token } = this;
        let paymentUrl = `${
            useBaseUrls().auth
        }/api/charge/pay/${amount}?access_token=${token}`;
        if (reason) {
            paymentUrl += `&reason=${reason}`;
        }
        window.location.href = paymentUrl;
    }

    /**
     * ACTION — Loads the report of credit
     *
     * @memberof AuthStore
     */
    async loadCreditTransactions() {
        this.creditTransactionsLoading = true;
        if (this.loggedIn) {
            try {
                const { data } = await UserTransactionApiApiUserTransactionsGet(
                    {
                        page: 1,
                        pageSize: 1000,
                    }
                );
                this.creditTransactionsLoading = false;
                this.creditTransactions = data.result || [];
            } catch (ex) {
                handleException(ex);
                this.creditTransactionsLoading = false;
            }
        }
    }

    /**
     * ACTION - Loads the current score and loyalty level of the user
     *
     * @param {boolean} forceReload
     * @memberof AuthStore
     */
    async loadScore(forceReload = false) {
        if (this.loggedIn && (forceReload || this.score === null)) {
            const { data } = await UserScoresApiApiUserScoresBriefGet();
            this.score = data.Score || 0;
            this.scoreDiscountAmount = data.MaximumApplicableDiscount || 0;
            this.level = data.UserLevel || UserScoreLevels.Level1;
            this.totalTickets = data.BoughtTicketsCount || 0;
            this.canGetDoubleScore = data.CanGetAppDoubleScore || false;
            await this.loadScoreDomains();
            const { data: details } =
                await UserScoresApiApiUserScoresVariationGet();
            this.levelDetails = details;
        }
    }

    /**
     * ACTION - Loads users information from server
     *
     * @param {boolean} forceReload
     * @param index
     * @memberof AuthStore
     */
    async loadUser(forceReload = false, index = this.selectedCredentialIndex) {
        this.termsStatus = this.authStorage.getItem(
            AuthLocalStorageKeys.termsStatus
        ) as TermsStatus;
        if (
            !this.userIsLoading &&
            this.loggedIn &&
            (forceReload || this.userInfo === null)
        ) {
            this.userIsLoading = true;
            try {
                const { data } = await UserApiApiGetuserGet();
                if (data.tokenRefreshRequired) {
                    await this.refreshToken();
                }
                if (!Object.keys(data).length) {
                    if (this.credentials?.length) {
                        this.logout();
                    }
                    return;
                }
                if (this.credentials?.some(c => c.title === '')) {
                    // can be removed after a while
                    this.credentials = this.credentials.map(c => {
                        if (c.title === '') {
                            return {
                                ...c,
                                title: `${data.title} ${data.lastName}`,
                            };
                        }
                        return c;
                    });

                    this.authStorage.setItem(
                        AuthLocalStorageKeys.credentials,
                        JSON.stringify(this.credentials)
                    );
                }

                this.userInfo = data;
                if (index === 0) {
                    this.initialContact = {
                        mobile: data.mobile ?? '',
                        email: data.email ?? '',
                        tel: data.tel ?? '',
                    };
                }

                this.lastCheckoutTimeout();
                useSearchFormStore().init();
                await Promise.all([
                    await this.loadScore(true),
                    await this.checkUserHasUnreadMessage(),
                    await this.loadUserCustomDiscounts(),
                ]);
                await this.checkUserHasPurchase();
                this.webengageLogin();
                this.metrixLogin();
                this.clarityLogin();
                this.gtmLogin(data.id ?? 0);
            } catch (ex) {
                const e = ex as any;
                if (
                    e?.response?.status === 401 ||
                    e?.response?.status === 403
                ) {
                    this.logout();
                } else {
                    throw ex;
                }
            } finally {
                this.userIsLoading = false;
            }
        } else if (this.selectedCredentialIndex !== index) {
            try {
                const { data } = await UserApiApiGetuserGet(undefined, {
                    headers: {
                        Authorization: `Bearer ${this.credentials?.[0].token}`,
                    },
                });
                this.initialContact = {
                    mobile: data.mobile ?? '',
                    email: data.email ?? '',
                    tel: data.tel ?? '',
                };
            } catch (e) {
                reportException(e);
            }
        } else {
            this.webengageLogin();
            this.metrixLogin();
            this.clarityLogin();
        }
    }

    async refreshWebAppToken() {
        if (localStorage.getItem('web-app-token-refreshed') != null) {
            return;
        }
        if (!this.needTokenMigration) {
            return;
        }
        try {
            const { data } = await LoginApiMigrateAppTokenPost({
                migrateJwtRequest: {
                    token: this.token,
                },
            });
            this.setLoginInfo(data);
            localStorage.setItem('web-app-token-refreshed', 'true');
        } catch (e) {
            reportException(e);
        }
    }

    async initUser(refreshWebAppToken = false) {
        this.reset();
        // uuid(playerId) is only valid up to 2 months
        const credentialsExpireDate =
            this.credentials?.[this.selectedCredentialIndex]?.expireDateMilSec;
        if (
            credentialsExpireDate &&
            credentialsExpireDate - dayConfig.gregory().valueOf() < 0
        ) {
            this.logout();
            return;
        }

        if (refreshWebAppToken) {
            await this.refreshWebAppToken();
        }
        await this.loadUser();
    }

    lastCheckoutTimeout() {
        const isLastCheckoutAlertShown = sessionStorage.getItem(
            AuthLocalStorageKeys.lastCheckout
        );
        if (
            (this.userInfo?.lastStatusReportCheckoutDays || 0) >= 45 &&
            isLastCheckoutAlertShown !== 'shown'
        ) {
            sessionStorage.setItem(AuthLocalStorageKeys.lastCheckout, 'shown');
            swal({
                title: 'مشتری گرامی',
                text: `از آخرین صورت وضعیت پرداخت نشده شما ${this.userInfo?.lastStatusReportCheckoutDays} روز گذشته است، جهت جلوگیری از غیرفعال شدن پنل نسبت به تسویه بدهکاری اقدام فرمایید.`,
                icon: 'info',
                confirmButtonText: 'بستن',
            });
        }
    }

    async refreshToken() {
        try {
            const { data } = await LoginApiRefreshPost({
                refreshExpiredJwtRequest: {
                    token: this.token,
                },
            });
            this.setLoginInfo(data);
        } catch (e) {
            reportException(e);
        }
    }

    /**
     * ACTION - Select an account type after login
     *
     * @memberof AuthStore
     */
    async selectAccount(index: number) {
        this.setSelectedCredentialIndex(index);
        await this.success();
    }

    /**
     * ACTION - Associates unique user id to user
     *
     * @memberof AuthStore
     */
    async associateUuid() {
        if (!this.uuid) {
            return;
        }
        const { data } = await UserApiApiAssociateUUIDGet({ uuid: this.uuid });
        if (data) {
            this.setLocalUuid(generateUUID());
        }
    }

    async updateUserInfo(user: Partial<RegisterRequest>) {
        await UserApiApiChangeUserPatch({
            registerRequest: user as RegisterRequest,
        });
        this.loadUser(true);
    }

    sendUTM(utm: Utm) {
        const campaignRequest: CampaignRequest = {
            medium: utm.utmCampaign ?? null,
            campaign: utm.utmMedium ?? null,
            creative: utm.utmCreative ?? null,
            source: utm.utmSource ?? null,
            campaignType: CampaignType.Utm,
            key: utm.utmKey ?? null,
            serviceType: utm.utmServiceType ?? null,
            url: utm.url ?? null,
            uuid: this.uuid,
            referrer: utm.referrer,
        };

        if (utm.utmSource) {
            LocalStorageUtils.setItem(
                AuthLocalStorageKeys.utmCache,
                {
                    UtmCampaign: utm.utmCampaign,
                    UtmMedium: utm.utmMedium,
                    UtmSource: utm.utmSource,
                    UtmContent: utm.utmContent,
                    UtmTerm: utm.utmTerm,
                    UtmId: utm.utmId,
                } as UtmAnalyticsParamRequest,
                dayConfig.gregory().add(3, 'day').toDate()
            );
        }
        if (utm.utmSource === 'takhfifan') {
            const now = new Date();
            now.setDate(now.getDate() + 7);
            LocalStorageUtils.setItem(AuthLocalStorageKeys.takhfifan, utm, now);
            CampaignApiApiSetUUIDCampaignPost({ campaignRequest });
        } else {
            const val = this.authStorage.getItem(AuthLocalStorageKeys.utm);
            const prevList = JSON.parse(val || '[]') as Utm[];
            if (utm.utmSource === 'rayanCard') {
                sessionStorage.setItem(
                    AuthSessionStorageKeys.rayanCard,
                    'true'
                );
            } else if (utm.utmSource === 'tara') {
                sessionStorage.setItem(AuthSessionStorageKeys.tara, 'true');
            }
            if (
                !prevList.find(
                    item =>
                        item.utmCampaign === utm.utmCampaign &&
                        item.utmMedium === utm.utmMedium &&
                        item.utmSource === utm.utmSource &&
                        item.utmCreative === utm.utmCreative
                )
            ) {
                CampaignApiApiSetUUIDCampaignPost({ campaignRequest });
            }
            prevList.push(utm);
            this.authStorage.setItem(
                AuthLocalStorageKeys.utm,
                JSON.stringify(prevList)
            );
        }
    }

    //#region user access
    async loadUserPaymentLimit() {
        if (this.isOrganizationalUser && !this.loadingUserLimit) {
            try {
                this.loadingUserLimit = true;
                const { data } =
                    await StatusReportSummaryApiUserReportIsCreditPaymentLimitedGet();
                this.limitPayment = !!data.isLimited;
            } catch (e) {
                reportException(e);
            } finally {
                this.loadingUserLimit = false;
            }
        }
    }
    //#endregion

    signupMagically({
        signupMac,
        mobile,
    }: {
        mobile: string;
        signupMac: string;
    }) {
        this.signupMac = signupMac;
        const { login } = useAuth();
        login({ mobile, step: AuthStep.SignupInfo });
    }

    async checkUserHasUnreadMessage() {
        if (!this.loggedIn) {
            return;
        }
        try {
            const {
                data: { data },
            } = await UserMessagesApiApiCheckUnreadGet();
            this.userHasUnreadMessage = !!data?.count;
        } catch (e) {
            reportException(e);
        }
    }
}
export const { use: useAuthStore } = defineClassStore(
    'auth',
    () => new AuthStore()
);
